<script lang="ts">
	// @ts-nocheck
	import { page } from '$app/stores';
	import { onMount } from 'svelte';
	import Button from '$lib/components/ui/button/button.svelte';
	import { Facebook, Instagram, Linkedin, Mail, Twitter, Youtube } from 'lucide-svelte';
	import Featured from '$lib/components/home/Featured.svelte';
	import Seo from '$lib/components/SEO.svelte';
	import { blogImages } from '$lib/utils/images';
	// Using dynamic imports for images
	let { data } = $props();

	let currentArticleUrl = $derived($page.url.href);

	const blog = data.blog;

	if (!blog) {
		throw new Error('Blog not found');
	}

	function parseBlogDate(dateStr: string): string {
		const [day, month, year] = dateStr.split('/');
		return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
	}

	const blogImageModule = blogImages[blog.image] as any;

	let blogPostingSchema = $derived({
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		headline: blog.title,
		description: blog.shortDescription,
		...(blogImageModule?.default?.img?.src
			? { image: `${$page.url.origin}${blogImageModule.default.img.src}` }
			: {}),
		datePublished: parseBlogDate(blog.uploaded),
		author: {
			'@type': 'Organization',
			name: 'Tassie Tours',
			url: $page.url.origin
		},
		publisher: {
			'@type': 'Organization',
			name: 'Tassie Tours',
			logo: {
				'@type': 'ImageObject',
				url: `${$page.url.origin}/favicon.png`
			}
		},
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': $page.url.href
		}
	});

	let blogBreadcrumbSchema = $derived({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: $page.url.origin },
			{ '@type': 'ListItem', position: 2, name: 'Blog', item: `${$page.url.origin}/blog` },
			{ '@type': 'ListItem', position: 3, name: blog.title, item: $page.url.href }
		]
	});

	let faqSchema = $derived(
		blog.faq?.length
			? {
					'@context': 'https://schema.org',
					'@type': 'FAQPage',
					mainEntity: blog.faq.map((item) => ({
						'@type': 'Question',
						name: item.question,
						acceptedAnswer: {
							'@type': 'Answer',
							text: item.answer
						}
					}))
				}
			: null
	);

	let blogSchemaScript = $derived(
		`<script type="application/ld+json">${JSON.stringify(blogPostingSchema)}${'<'}/script>` +
			`<script type="application/ld+json">${JSON.stringify(blogBreadcrumbSchema)}${'<'}/script>` +
			(faqSchema
				? `<script type="application/ld+json">${JSON.stringify(faqSchema)}${'<'}/script>`
				: '')
	);

	function shareOnTwitter() {
		let tweetText = 'Check out this article from Tassie Tours!'; // Customize tweet text
		let twitterUrl = `https://twitter.com/intent/tweet?url=${currentArticleUrl}&text=${tweetText}`;
		window.open(twitterUrl, '_blank');
	}
	let animate = false;

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);
		animate = true;
	});
</script>

<svelte:head>
	{@html blogSchemaScript}
</svelte:head>

<Seo
	title={blog.metaTitle || blog.title}
	description={blog.shortDescription}
	imageURL={blog.image}
	author="Tassie Tours"
	type="article"
	name="Tassie Tours"
	index={true}
	twitter={true}
	openGraph={true}
	schemaOrg={true}
/>

<nav aria-label="Breadcrumb" class="mx-auto max-w-7xl px-4 py-3">
	<ol class="flex items-center gap-2 text-sm text-muted-foreground">
		<li><a href="/" class="transition-colors hover:text-foreground">Home</a></li>
		<li aria-hidden="true">/</li>
		<li><a href="/blog" class="transition-colors hover:text-foreground">Blog</a></li>
		<li aria-hidden="true">/</li>
		<li class="line-clamp-1 font-medium text-foreground" aria-current="page">{blog.title}</li>
	</ol>
</nav>

<section class="relative mx-auto flex h-[600px] flex-col items-center justify-center">
	<enhanced:img
		src={Object.entries(blogImages).find(([key, value]) => key === blog.image)[1].default}
		alt={blog.title}
		class="absolute inset-0 h-[600px] w-full object-cover"
		sizes="(min-width: 1280px) 1280px, 100vw"
		fetchpriority="high"
	/>

	<div class="absolute inset-0 bg-foreground/50"></div>

	<div
		class="pointer-events-none absolute inset-0 mx-auto flex h-[600px] w-full max-w-7xl flex-col justify-center px-4"
	>
		<h1
			class="mx-auto max-w-2xl scroll-m-20 text-center font-heading text-2xl font-bold tracking-tight text-background lg:mx-0 lg:text-left lg:text-4xl"
		>
			{blog.title}
		</h1>
		<div class="mx-auto mt-6 flex max-w-xl items-center lg:mx-0">
			<p class="text-md mx-auto text-center text-background lg:mx-0 lg:text-left">
				{blog.shortDescription}
			</p>
		</div>
		<div class="mt-6 flex flex-col gap-4 font-medium text-background lg:flex-row">
			<div class="mx-auto flex items-center lg:mx-0">
				<p class="lg:text-md text-sm italic">{blog.date} • {blog.category}</p>
			</div>
		</div>
		<div
			class="pointer-events-auto mt-6 flex items-center justify-center gap-2 font-medium text-background lg:justify-start"
		>
			<Button
				variant="ghost"
				size="icon"
				href={`https://www.facebook.com/sharer/sharer.php?u=${currentArticleUrl}`}
				target="_blank"
				class="rounded-full p-0 text-background/80 hover:bg-background/20 hover:text-background"
			>
				<span class="sr-only">Facebook</span>
				<Facebook class=" !h-6 !w-6" />
			</Button>
			<Button
				variant="ghost"
				size="icon"
				onclick={shareOnTwitter}
				class="rounded-full text-background/80 hover:bg-background/20 hover:text-background"
			>
				<span class="sr-only">Twitter</span>
				<Twitter class=" !h-6 !w-6" />
			</Button>
			<Button
				variant="ghost"
				size="icon"
				href={`https://www.linkedin.com/sharing/share-offsite/?url=${currentArticleUrl}`}
				target="_blank"
				class="rounded-full text-background/80 hover:bg-background/20 hover:text-background"
			>
				<span class="sr-only">Linked In</span>
				<Linkedin class=" !h-6 !w-6" />
			</Button>
			<Button
				variant="ghost"
				size="icon"
				href={`mailto:?subject=Check out this article from Tassie Tours&body=We should plan a trip: ${currentArticleUrl}`}
				target="_blank"
				class="rounded-full text-background/80 hover:bg-background/20 hover:text-background"
			>
				<span class="sr-only">Email</span>
				<Mail class=" !h-6 !w-6" />
			</Button>
		</div>
	</div>
</section>

<main class="mx-auto max-w-5xl px-4 py-12 lg:pt-24">
	{@html blog.content}
</main>

<Featured heading="View our popular tours" />
